/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Query, Brick, PropertyModel, BrickContext, registerBrick, CloudObject, Transaction, RelationModel, ErrorFlow, DBView, StringModel, NumberModel, DatetimeModel, BooleanModel } from 'olympe';

// Caches to speed up conversion of large numbers of objects
const TYPES = { STRING: 0, NUMBER: 1, BOOLEAN: 2, DATETIME: 3 };
const modelPropsMap = new Map();
const propTypesMap = new Map();

export default class JSONToCloudObject extends Brick {
    /**
     * @override
     * @param {!BrickContext} $
     * @param {Object | string} source
     * @param {!CloudObject} businessModel
     * @param {boolean} persist
     * @param {function()} forwardEvent
     * @param {function(!ErrorFlow)} setErrorFlow
     * @param {function(!Array | !CloudObject)} setResult
     */
    update($, [source, businessModel, persist], [forwardEvent, setResult, setErrorFlow]) {
        // Guards
        if (source === null) {
            setErrorFlow(ErrorFlow.create('Provided source null', 1));
            return;
        }
        if (typeof source !== 'object' && typeof source !== 'string') {
            setErrorFlow(ErrorFlow.create('Provided source is not an object or a string', 1));
            return;
        }
        if (!(businessModel instanceof CloudObject)) {
            setErrorFlow(ErrorFlow.create('Provided model is not a Type', 1));
            return;
        }

        let json;
        try {
            // Try to parse the json if it is a string or keep the object otherwise.
            json = (typeof source === 'string') ? JSON.parse(source) : source;
        } catch(e) {
            setErrorFlow(ErrorFlow.create(`Error while parsing the source string: ${e.message}`, 2));
            return;
        }

        const transaction = new Transaction(persist);

        // Result is either an array of tags or a single tag depending on the JSON source.
        const result = Array.isArray(json)
            ? json.map((data) => this.parseInstance(transaction, businessModel, data))
            : this.parseInstance(transaction, businessModel, json);

        transaction.execute().then(() => {
            setResult(Array.isArray(result) ? result.map(CloudObject.get) : CloudObject.get(result));
            forwardEvent();
        }).catch(message => {
            setErrorFlow(ErrorFlow.create('Transaction failed: ' + message, 4));
        });
    }

    /**
     * @private
     * @param {!Transaction} transaction
     * @param {!CloudObject} model
     * @param {!Object} data
     * @return {string}
     */
    parseInstance(transaction, model, data) {
        const properties = this.parseProperties(model, data);
        const instanceTag = transaction.create(model, properties);
        this.parseRelations(transaction, instanceTag, model, data);
        return instanceTag;
    }

    /**
     * @private
     * @param {!CloudObject} model
     * @param {!Object} data
     * @return {!Map<Tag, *>}
     */
    parseProperties(model, data) {
        let props = modelPropsMap.get(model);
        // Fill in the map if not already filled
        if (!props) {
            props = Query.from(model)
                .followRecursively(CloudObject.extendRel, true)
                .executeFromCache()
                .filter((m) => !m.equals(CloudObject.asInstance()))
                .flatMap((m) => m.follow(CloudObject.propertyRel).executeFromCache().toArray());
            modelPropsMap.set(model, props);
        }

        return props.reduce((map, property) => {
            const value = data[property.name()];
            if (value !== undefined && (!(value instanceof Array) && !(value instanceof Object))) {
                map.set(property, this.formatValue(value, property));
            }
            return map;
        }, new Map());
    }

    /**
     * @private
     * @param {*} value
     * @param {!CloudObject} property
     * @return {*}
     */
    formatValue(value, property) {
        let type = propTypesMap.get(property);
        // Fill in the map if not already filled
        if (type === undefined) {
            const propType = property.followSingle(PropertyModel.typeRel).executeFromCache();
            switch (true) {
                case propType === null:
                    type = null;
                    break;
                case DBView.get().isExtending(propType, StringModel):
                    type = TYPES.STRING;
                    break;
                case propType.equals(NumberModel.asInstance()):
                    type = TYPES.NUMBER;
                    break;
                case propType.equals(DatetimeModel.asInstance()):
                    type = TYPES.DATETIME;
                    break;
                case propType.equals(BooleanModel.asInstance()):
                    type = TYPES.BOOLEAN;
                    break;
                default:
                    type = null;
            }
            propTypesMap.set(property, type);
        }

        // null case
        if(value === null) {
            return null;
        }

        switch (type) {
            case TYPES.STRING:
                return String(value);
            case TYPES.NUMBER:
                return Number(value);
            case TYPES.DATETIME:
                return value instanceof Date ? value : new Date(value);
            case TYPES.BOOLEAN:
                return !!value;
            default:
                return null;
        }
    }

    /**
     * @private
     * @param {!Transaction} transaction
     * @param {string} instance
     * @param {!CloudObject} model
     * @param {!Object} data
     */
    parseRelations(transaction, instance, model, data) {
        const createDestinationObject = (relation, destinationObject) => {
            const destinationModel = relation.followSingle(RelationModel.destinationModelRel).executeFromCache();
            const relatedInstance = this.parseInstance(transaction, destinationModel, destinationObject);
            transaction.createRelation(relation, instance, relatedInstance);
        };

        const relations = Query.from(model).followRecursively(CloudObject.extendRel, true).executeFromCache()
            .filter((model) => !model.equals(CloudObject.asInstance()))
            .flatMap((model) => model.follow(RelationModel.originModelRel.getInverse()).executeFromCache().toArray());
        relations.forEach((relation) => {
            const destinationObject = data[relation.name()];
            if (Array.isArray(destinationObject)) {
                destinationObject.forEach(createDestinationObject.bind(this, relation));
            } else if (destinationObject instanceof Object) {
                createDestinationObject(relation, destinationObject);
            }
        });
    }
}

registerBrick('018162fcbe206603fdf5', JSONToCloudObject);
